package fr.manyverse.app.presentation.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.presentation.components.CharacterCard
import fr.manyverse.app.presentation.components.CharacterEditForm
import fr.manyverse.app.presentation.components.ErrorState
import fr.manyverse.app.presentation.components.SkeletonList
import fr.manyverse.app.presentation.layout.WindowSizeContainer
import fr.manyverse.app.presentation.layout.WindowWidth
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.CharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.back
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.confirm
import manyverse.shared.generated.resources.create_character
import manyverse.shared.generated.resources.delete_character_in_table_message
import manyverse.shared.generated.resources.delete_character_in_table_title
import manyverse.shared.generated.resources.error_loading_characters
import manyverse.shared.generated.resources.my_characters
import manyverse.shared.generated.resources.no_characters_yet
import manyverse.shared.generated.resources.retry
import manyverse.shared.generated.resources.select_character_prompt
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CharacterListScreen(
    userId: String,
    onNavigateBack: () -> Unit,
    onCreateCharacter: () -> Unit,
    onEditCharacter: (String) -> Unit = {},
    modifier: Modifier = Modifier,
    viewModel: CharacterViewModel = koinViewModel()
) {
    val uiState by viewModel.characterListState.collectAsStateWithLifecycle()
    val pendingDelete by viewModel.pendingDelete.collectAsStateWithLifecycle()
    val universesById by viewModel.universesById.collectAsStateWithLifecycle()
    var selectedCharacterId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(userId) {
        viewModel.loadAllCharacters(userId)
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.my_characters)) },
                navigationIcon = {
                    IconButton(
                        onClick = onNavigateBack,
                        modifier = Modifier.testTag("backButton")
                    ) {
                        Icon(
                            Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = stringResource(Res.string.back)
                        )
                    }
                }
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = onCreateCharacter,
                containerColor = MaterialTheme.colorScheme.primary,
                modifier = Modifier.testTag("createCharacterFAB")
            ) {
                Icon(
                    Icons.Default.Add,
                    contentDescription = stringResource(Res.string.create_character)
                )
            }
        },
        modifier = modifier
    ) { padding ->
        when (val state = uiState) {
            // The list loads silently (no Loading state), so show the skeleton on the
            // initial Idle pass too, otherwise the first frame is a blank screen.
            is AsyncUiState.Idle,
            is AsyncUiState.Loading -> {
                SkeletonList(
                    modifier = Modifier
                        .padding(padding)
                        .testTag("loadingBox")
                )
            }

            is AsyncUiState.Success -> {
                if (state.data.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(padding)
                            .testTag("emptyStateBox"),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = stringResource(Res.string.no_characters_yet),
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.testTag("emptyStateText")
                        )
                    }
                } else {
                    WindowSizeContainer(modifier = Modifier.padding(padding)) { windowWidth ->
                        if (windowWidth == WindowWidth.EXPANDED) {
                            // List-detail: grid on the left, inline edit pane on the right.
                            Row(modifier = Modifier.fillMaxSize()) {
                                CharacterGrid(
                                    characters = state.data,
                                    universesById = universesById,
                                    onEditCharacter = { selectedCharacterId = it },
                                    modifier = Modifier.weight(1f)
                                )
                                VerticalDivider()
                                val selected = state.data.find { it.id == selectedCharacterId }
                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .fillMaxHeight()
                                        .testTag("characterDetailPane"),
                                    contentAlignment = Alignment.Center
                                ) {
                                    if (selected != null) {
                                        CharacterEditForm(
                                            userId = userId,
                                            character = selected,
                                            viewModel = viewModel,
                                            onCancel = { selectedCharacterId = null }
                                        )
                                    } else {
                                        Text(
                                            text = stringResource(Res.string.select_character_prompt),
                                            style = MaterialTheme.typography.bodyLarge,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                                            modifier = Modifier
                                                .padding(24.dp)
                                                .testTag("selectCharacterPrompt")
                                        )
                                    }
                                }
                            }
                        } else {
                            CharacterGrid(
                                characters = state.data,
                                universesById = universesById,
                                onEditCharacter = onEditCharacter,
                                modifier = Modifier.fillMaxSize()
                            )
                        }
                    }
                }
            }

            is AsyncUiState.Error -> {
                ErrorState(
                    message = state.message,
                    onRetry = { viewModel.loadAllCharacters(userId) },
                    title = stringResource(Res.string.error_loading_characters),
                    modifier = Modifier.padding(padding)
                )
            }
        }
    }

    pendingDelete?.let { pending ->
        AlertDialog(
            onDismissRequest = { viewModel.cancelDeleteCharacter() },
            title = { Text(stringResource(Res.string.delete_character_in_table_title)) },
            text = {
                Text(
                    stringResource(
                        Res.string.delete_character_in_table_message,
                        pending.character.name,
                        pending.tableName
                    )
                )
            },
            confirmButton = {
                TextButton(
                    onClick = { viewModel.confirmDeleteCharacter() },
                    modifier = Modifier.testTag("confirmDeleteCharacterButton")
                ) {
                    Text(stringResource(Res.string.confirm))
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { viewModel.cancelDeleteCharacter() },
                    modifier = Modifier.testTag("cancelDeleteCharacterButton")
                ) {
                    Text(stringResource(Res.string.cancel))
                }
            }
        )
    }
}

@Composable
private fun CharacterGrid(
    characters: List<Character>,
    universesById: Map<String, Universe>,
    onEditCharacter: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    LazyVerticalGrid(
        columns = GridCells.Adaptive(minSize = 300.dp),
        contentPadding = PaddingValues(16.dp),
        horizontalArrangement = Arrangement.spacedBy(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp),
        modifier = modifier.testTag("characterGrid")
    ) {
        items(
            items = characters,
            key = { it.id }
        ) { character ->
            CharacterCard(
                character = character,
                universe = universesById[character.universeId],
                onEditCharacter = onEditCharacter
            )
        }
    }
}
